import React, { createContext, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { DefinitionPreview, getDefinitionPreview } from '../../lookupApi';
import { collectConceptLinks, conceptSegments, definitionTerm, eurLexTextDestination } from './conceptLinks';
import { citedSubPart, conceptTarget, libraryTarget, type LibraryTarget } from './libraryLinks';
import { useLibraryPanel } from './LibraryPanel';

type Loader = (url: string) => Promise<DefinitionPreview>;
const Definitions = createContext<Loader | null>(null);
const LINK_CLASS = 'text-accent hover:text-accent-hover underline';

/** Cache only within the mounted chat/account. Failed reads can be retried;
 *  leaving the chat cancels in-flight reads and clears their cache. */
export function DefinitionPreviewProvider({ token, chatId, children }: { token: string; chatId?: string; children: React.ReactNode }) {
    const session = useMemo(() => {
        const cache = new Map<string, Promise<DefinitionPreview>>();
        const controllers = new Set<AbortController>();
        const load: Loader = url => {
            const existing = cache.get(url);
            if (existing) return existing;
            const controller = new AbortController();
            controllers.add(controller);
            const pending = getDefinitionPreview(url, token, controller.signal, chatId)
                .catch(error => { if (cache.get(url) === pending) cache.delete(url); throw error; })
                .finally(() => controllers.delete(controller));
            cache.set(url, pending);
            return pending;
        };
        return { load, clear: () => { controllers.forEach(controller => controller.abort()); cache.clear(); } };
    }, [token, chatId]);
    useEffect(() => () => session.clear(), [session]);
    return <Definitions.Provider value={session.load}>{children}</Definitions.Provider>;
}

/** When the anchor's href is a rewrite (EUR-Lex HTML → Text view), the link
 *  as the answer wrote it, for analytics (`answerLinkClicks`). */
const answerHref = (href: string, destination: string) => (destination !== href ? { 'data-answer-href': href } : undefined);

/** The panel's header: the link's text, or "Article 92" when the text is
 *  only the number ("Articles 89, [90] and 91"). */
const panelLabel = (text: string, target: LibraryTarget) => (/[a-z]/i.test(text) || !target.article ? text : target.article);

/** A plain left click (no modifier: those keep the browser's new tab, window or download). */
const plainClick = (event: React.MouseEvent) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

export default function ConceptLink({ href, children, concept = false }: { href: string; children: React.ReactNode; concept?: boolean }) {
    const load = useContext(Definitions);
    const panel = useLibraryPanel();
    if (!load || !concept || !definitionTerm(href)) {
        const destination = load ? eurLexTextDestination(href) : href;
        // In Chat, a EUR-Lex link the Library can show opens it in the side
        // panel; a modified click (new tab, window, download) keeps the link.
        const library = panel ? libraryTarget(href, panel.origin) : null;
        return <a href={destination} {...answerHref(href, destination)} target="_blank" rel="noopener noreferrer" className={LINK_CLASS} onClick={event => {
            event.stopPropagation();
            if (!panel || !library || !plainClick(event)) return;
            event.preventDefault();
            const text = (event.currentTarget.textContent || '').trim();
            // "Art 4(1)(36)": the Library also marks and scrolls to that point of the article
            const at = citedSubPart(text, library.article);
            const target = at ? libraryTarget(href, panel.origin, at) || library : library;
            panel.open({ target, eurlex: destination, label: panelLabel(text, target) }, event.currentTarget);
        }}>{children}</a>;
    }
    return <DefinitionLink key={href} href={href} load={load}>{children}</DefinitionLink>;
}

function DefinitionLink({ href, children, load }: { href: string; children: React.ReactNode; load: Loader }) {
    const destination = eurLexTextDestination(href);
    const panel = useLibraryPanel();
    // In Chat a click opens the term's concept page in the Library panel; hover
    // and focus still preview the definition here.
    const library = panel ? conceptTarget(href, panel.origin) : null;
    const id = useId();
    const anchor = useRef<HTMLAnchorElement>(null);
    const bubble = useRef<HTMLDivElement>(null);
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState<DefinitionPreview>();
    const segments = useMemo(() => preview
        ? conceptSegments(preview.text, collectConceptLinks('', [
            ...(preview.concept_terms || []), { term: definitionTerm(href) || '', url: href },
        ])) : [], [preview, href]);
    const [failed, setFailed] = useState(false);
    const [position, setPosition] = useState({ left: 0, top: 0 });
    const cancelTimer = () => clearTimeout(timer.current);
    const show = () => { cancelTimer(); setOpen(true); };
    const close = () => { cancelTimer(); setOpen(false); };
    const leave = () => {
        cancelTimer();
        timer.current = setTimeout(() => {
            if (!bubble.current?.contains(document.activeElement) && document.activeElement !== anchor.current) setOpen(false);
        }, 200);
    };
    useEffect(() => () => clearTimeout(timer.current), []);
    useEffect(() => {
        if (!open) return;
        let current = true;
        setPreview(undefined);
        setFailed(false);
        load(href).then(value => { if (current) setPreview(value); }, () => { if (current) setFailed(true); });
        return () => { current = false; };
    }, [open, href, load]);

    useLayoutEffect(() => {
        if (!open) return;
        const place = () => {
            if (!anchor.current || !bubble.current) return;
            const trigger = anchor.current.getBoundingClientRect();
            const bounds = bubble.current.getBoundingClientRect();
            const style = getComputedStyle(document.documentElement);
            const gutter = parseFloat(style.getPropertyValue('--space-4')) || 0;
            const gap = parseFloat(style.getPropertyValue('--space-2')) || 0;
            const below = trigger.bottom + gap;
            setPosition({
                left: Math.max(gutter, Math.min(trigger.left, window.innerWidth - bounds.width - gutter)),
                top: Math.max(gutter, below + bounds.height <= window.innerHeight - gutter ? below : trigger.top - bounds.height - gap),
            });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        const dismiss = (event: PointerEvent) => {
            if (!bubble.current?.contains(event.target as Node) && !anchor.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', dismiss);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
            document.removeEventListener('pointerdown', dismiss);
        };
    }, [open, preview, failed]);

    const openLibrary = () => {
        if (!panel || !library || !anchor.current) return;
        close();
        panel.open({ target: library, eurlex: destination, label: (anchor.current.textContent || '').trim() || definitionTerm(href) || '' }, anchor.current);
    };
    const blur = (event: React.FocusEvent) => {
        if (!bubble.current?.contains(event.relatedTarget as Node) && event.relatedTarget !== anchor.current) close();
    };
    const escape = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.preventDefault(); event.stopPropagation();
            anchor.current?.focus(); close();
        }
    };
    return <>
        <a ref={anchor} href={destination} {...answerHref(href, destination)} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}
            aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
            onMouseEnter={() => { cancelTimer(); timer.current = setTimeout(show, 250); }} onMouseLeave={leave}
            onFocus={show} onBlur={blur}
            onClick={event => {
                event.stopPropagation();
                if (!plainClick(event)) return;
                event.preventDefault();
                if (library) openLibrary(); else show();
            }}
            onKeyDown={event => {
                escape(event);
                if (open && event.key === 'Tab' && !event.shiftKey) {
                    event.preventDefault(); bubble.current?.querySelector<HTMLButtonElement>('button')?.focus();
                }
            }}>{children}</a>
        {open && createPortal(
            <div ref={bubble} id={id} role="dialog" aria-labelledby={`${id}-title`}
                className="fixed z-50 w-80 rounded-lg border border-border-default bg-surface p-4 text-sm text-fg shadow-bark-md overflow-auto"
                style={{ ...position, maxWidth: 'calc(100vw - var(--space-8))', maxHeight: 'calc(100dvh - var(--space-8))' }}
                onMouseEnter={cancelTimer} onMouseLeave={leave} onBlur={blur} onKeyDown={escape} onClick={event => event.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 mb-2">
                    <span id={`${id}-title`} className="font-semibold">{children}</span>
                    <button type="button" aria-label="Close definition" className="text-fg-tertiary hover:text-fg rounded focus-visible:shadow-bark-focus"
                        onClick={() => { anchor.current?.focus(); close(); }}>×</button>
                </div>
                {!preview && !failed && <p role="status" className="flex items-center gap-2">
                    <Loader2 size={16} strokeWidth={1.5} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    Loading definition…
                </p>}
                {failed && <p role="status">Preview unavailable. You can still open the definition in EUR-Lex.</p>}
                {preview && <>
                    <p className="whitespace-pre-wrap leading-relaxed">{segments.map((part, index) => part.url && eurLexTextDestination(part.url) !== destination
                        ? <ConceptLink key={index} href={part.url}>{part.text}</ConceptLink> : part.text)}</p>
                    <p className="mt-2 text-xs text-fg-tertiary">{preview.locator} · EUR-Lex{preview.truncated ? ' · Preview' : ''}</p>
                    {preview.notice && <p className="mt-2 text-xs text-fg-tertiary">{preview.notice}</p>}
                </>}
                <div className="mt-3 flex flex-wrap gap-2">
                    <a href={destination} target="_blank" rel="noopener noreferrer"
                        className="inline-block rounded-md bg-primary px-3 py-2 text-xs text-fg-inverse hover:bg-primary-hover focus-visible:shadow-bark-focus">
                        {preview?.truncated ? 'Read full definition on EUR-Lex' : 'Open in EUR-Lex'}
                    </a>
                    {library && <button type="button" onClick={openLibrary}
                        className="inline-block rounded-md bg-primary px-3 py-2 text-xs text-fg-inverse hover:bg-primary-hover focus-visible:shadow-bark-focus">
                        Open in Library
                    </button>}
                </div>
            </div>, document.body,
        )}
    </>;
}
